import type {
  FlowDefinition,
  Participant,
  DiagramStep,
  MessageStep,
  SelfStep,
  NoteStep,
  GroupStep,
} from './SequenceDiagram'

const PALETTE: Array<Pick<Participant, 'headerBg' | 'headerBorder' | 'headerText'>> = [
  { headerBg: '#f5f3ff', headerBorder: '#c4b5fd', headerText: '#6d28d9' }, // purple
  { headerBg: '#eff6ff', headerBorder: '#93c5fd', headerText: '#1d4ed8' }, // blue
  { headerBg: '#f0fdf4', headerBorder: '#86efac', headerText: '#15803d' }, // green
  { headerBg: '#eef2ff', headerBorder: '#a5b4fc', headerText: '#4338ca' }, // indigo
  { headerBg: '#fff7ed', headerBorder: '#fdba74', headerText: '#c2410c' }, // orange
  { headerBg: '#f0fdfa', headerBorder: '#5eead4', headerText: '#0f766e' }, // teal
  { headerBg: '#fdf4ff', headerBorder: '#e879f9', headerText: '#a21caf' }, // fuchsia
  { headerBg: '#fff1f2', headerBorder: '#fda4af', headerText: '#be123c' }, // rose
]

export interface ParseResult {
  flow: FlowDefinition
  errors: string[]
}

export function parseDsl(id: string, dslText: string): ParseResult {
  const lines = dslText.split('\n')
  const errors: string[] = []
  let title = 'Untitled Flow'
  const participants: Participant[] = []
  const steps: DiagramStep[] = []
  const participantMap = new Map<string, Participant>()
  let colorIdx = 0

  function getOrCreate(pid: string): Participant {
    if (participantMap.has(pid)) return participantMap.get(pid)!
    const color = PALETTE[colorIdx % PALETTE.length]
    colorIdx++
    const p: Participant = { id: pid, label: pid, ...color }
    participantMap.set(pid, p)
    participants.push(p)
    return p
  }

  const groupStack: GroupStep[] = []
  let lastMessage: MessageStep | null = null

  function pushStep(step: DiagramStep) {
    lastMessage = null
    if (groupStack.length > 0) {
      const g = groupStack[groupStack.length - 1]
      if (step.type === 'message' || step.type === 'self' || step.type === 'note') {
        g.steps.push(step as MessageStep | SelfStep | NoteStep)
        if (step.type === 'message') lastMessage = step as MessageStep
      }
    } else {
      steps.push(step)
      if (step.type === 'message') lastMessage = step as MessageStep
    }
  }

  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i]
    const trimmed = raw.trim()
    if (!trimmed || trimmed.startsWith('#')) continue

    const isIndented = (raw.startsWith('  ') || raw.startsWith('\t')) && !!lastMessage
    if (isIndented) {
      const t = trimmed
      const epMatch = t.match(/^(GET|POST|PUT|PATCH|DELETE)\s+(.+)/)
      if (epMatch) {
        lastMessage!.endpoint = {
          method: epMatch[1] as 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
          url: epMatch[2].trim(),
        }
        continue
      }
      if (t.startsWith('>')) {
        try {
          lastMessage!.requestPayload = JSON.parse(t.slice(1).trim())
        } catch {
          errors.push(`Line ${i + 1}: invalid JSON in request payload`)
        }
        continue
      }
      if (t.startsWith('<')) {
        try {
          lastMessage!.responsePayload = JSON.parse(t.slice(1).trim())
        } catch {
          errors.push(`Line ${i + 1}: invalid JSON in response payload`)
        }
        continue
      }
    }

    if (!isIndented) lastMessage = null

    if (trimmed.startsWith('title ')) {
      title = trimmed.slice(6).trim()
      continue
    }

    if (trimmed.startsWith('participant ')) {
      const m = trimmed.match(/^participant\s+(\S+)(?:\s+"([^"]*)")?(?:\s+"([^"]*)")?/)
      if (m) {
        const [, pid, label, sublabel] = m
        const color = PALETTE[colorIdx % PALETTE.length]
        colorIdx++
        const p: Participant = { id: pid, label: label || pid, sublabel, ...color }
        participantMap.set(pid, p)
        participants.push(p)
      }
      continue
    }

    if (trimmed === '---') {
      pushStep({ type: 'spacer' })
      lastMessage = null
      continue
    }

    if (trimmed.startsWith('group ')) {
      const condition = trimmed.slice(6).trim()
      groupStack.push({ type: 'group', condition, steps: [] })
      lastMessage = null
      continue
    }

    if (trimmed === 'end') {
      const g = groupStack.pop()
      if (g) {
        if (groupStack.length > 0)
          groupStack[groupStack.length - 1].steps.push(g)
        else steps.push(g)
      }
      lastMessage = null
      continue
    }

    if (trimmed.startsWith('self ')) {
      const m = trimmed.match(/^self\s+(\S+):\s*(.+)/)
      if (m) {
        getOrCreate(m[1])
        pushStep({ type: 'self', participant: m[1], label: m[2].trim() })
      }
      continue
    }

    if (trimmed.startsWith('note ')) {
      const m = trimmed.match(/^note\s+(\S+):\s*(.+)/)
      if (m) {
        getOrCreate(m[1])
        pushStep({
          type: 'note',
          participant: m[1],
          lines: m[2].split('|').map((s) => s.trim()),
        })
      }
      continue
    }

    const msgM = trimmed.match(/^(\S+?)(->>{1}|-->{1}|->{1})(\S+?):\s*(.+)/)
    if (msgM) {
      const [, from, arrow, to, label] = msgM
      getOrCreate(from)
      getOrCreate(to)
      const step: MessageStep = {
        type: 'message',
        from,
        to,
        label: label.trim(),
        isReturn: arrow === '-->',
        isAsync: arrow === '->>',
      }
      pushStep(step)
      lastMessage = step
      continue
    }

    errors.push(`Line ${i + 1}: unrecognised syntax — "${trimmed.slice(0, 60)}"`)
  }

  return {
    flow: { id, title, participants, steps },
    errors,
  }
}

export interface DiagramTemplate {
  id: string
  label: string
  description: string
  dsl: string
}

export const DIAGRAM_TEMPLATES: DiagramTemplate[] = [
  {
    id: 'kyc-api',
    label: 'KYC API',
    description: 'Direct REST API integration for individual identity verification',
    dsl: `title KYC API — Direct Integration Flow

participant endUser "End User" "Customer"
participant clientFe "Client Frontend" "Your App UI"
participant clientBe "Client Backend" "Your Server"
participant frankieOne "FrankieOne" "API / Platform"
participant dataSources "Data Sources" "DVS, Experian..."

endUser->clientFe: Enters platform, submits personal details

clientFe->clientBe: Submit KYC data (name, DOB, address, ID document)

clientBe->frankieOne: Create individual entity
  POST https://api.uat.frankie.one/v2/individuals
  >{"individual":{"name":{"givenName":"JAMES","familyName":"TESTONE"},"dateOfBirth":"1990-01-01","addresses":[{"type":"RESIDENTIAL","country":"AUS"}],"consents":[{"type":"GENERAL"}]}}
  <{"individual":{"entityId":"BF-abc123..."}}

frankieOne-->clientBe: Return entityId

self clientBe: Map entityId to user and store

clientBe->frankieOne: Execute KYC workflow (returns synchronously)
  POST https://api.uat.frankie.one/v2/individuals/{entityId}/serviceprofiles/DEFAULT/workflows/{workflowName}/execute

group Workflow Name = AUS-Basic3V-TwoPlusID
  frankieOne->dataSources: KYC — DVS identity check
  dataSources-->frankieOne: DVS result
  frankieOne->dataSources: KYC — Experian credit bureau (if DVS insufficient)
  dataSources-->frankieOne: Experian result
  frankieOne->dataSources: KYC — Equifax credit bureau (if still insufficient)
  dataSources-->frankieOne: Equifax result
  frankieOne->dataSources: KYC — Superchoice superannuation (final)
  dataSources-->frankieOne: Superchoice result
  frankieOne->dataSources: AML — PEPs check
  dataSources-->frankieOne: PEPs result
  frankieOne->dataSources: AML — Sanctions check
  dataSources-->frankieOne: Sanctions result
end

frankieOne-->clientBe: Return full results synchronously (200 OK)
  <{"workflowResult":{"workflowExecutionState":"COMPLETED","individualProfileResult":"PASS"}}

self clientBe: Evaluate result — check individualProfileResult for PASS/FAIL/REFER

self clientBe: Store result and update application status in DB

clientBe-->clientFe: Return verification outcome

clientFe->endUser: Show verification result (approved / rejected / review)`,
  },
  {
    id: 'hosted-onesdk',
    label: 'Hosted OneSDK',
    description: 'Hosted verification page served by FrankieOne via iframe or shared link',
    dsl: `title Hosted OneSDK — KYC Flow

participant endUser "End User" "Customer"
participant clientFe "Client Frontend" "Your App UI"
participant clientBe "Client Backend" "Your Server"
participant frankieOne "FrankieOne" "API / Platform"
participant dataSources "Data Sources" "DVS, Experian..."

endUser->clientFe: Enters platform and starts onboarding flow

clientFe->clientBe: Request Hosted URL

clientBe->frankieOne: Generate Hosted URL
  POST https://api.uat.frankie.one/v2/individuals/hostedUrl
  >{"consentObtained":true,"oneSDKFlowId":"idv","redirectUrl":"https://your-app.com/kyc/complete"}
  <{"entityId":"BF-b50b1234...","url":"https://hosted.uat.frankie.one/kyc?token=eyJ..."}

frankieOne-->clientBe: Return Hosted URL + entityId

self clientBe: Store entityId → userId mapping

clientBe-->clientFe: Return Hosted URL

clientFe->endUser: Embed Hosted URL in iframe or share link

endUser->frankieOne: Opens Hosted URL, completes verification (welcome → consent → IDV → submit)

self frankieOne: Execute configured workflow on submitted data

group Workflow checks
  frankieOne->dataSources: KYC — DVS identity check
  dataSources-->frankieOne: DVS result
  frankieOne->dataSources: KYC — Experian credit bureau (if DVS insufficient)
  dataSources-->frankieOne: Experian result
  frankieOne->dataSources: KYC — Equifax credit bureau (if still insufficient)
  dataSources-->frankieOne: Equifax result
  frankieOne->dataSources: KYC — Superchoice superannuation (final)
  dataSources-->frankieOne: Superchoice result
  frankieOne->dataSources: AML — PEPs check
  dataSources-->frankieOne: PEPs result
  frankieOne->dataSources: AML — Sanctions check
  dataSources-->frankieOne: Sanctions result
end

frankieOne->endUser: Hosted page shows verification result to user

frankieOne->>clientBe: Webhook — workflow execution complete
  POST https://your-server.com/webhook/frankieone
  >{"entityId":"BF-b50b1234...","workflowExecutionId":"bf-wf-xxxx","status":"COMPLETE"}

clientBe->frankieOne: Fetch full results for entityId
  GET https://api.uat.frankie.one/v2/individuals/{entityId}

frankieOne-->clientBe: Return workflow execution results

self clientBe: Store result in DB`,
  },
  {
    id: 'kyb-api',
    label: 'KYB API',
    description: 'Business entity verification via FrankieOne REST API',
    dsl: `title KYB API — Business Verification Flow

participant endUser "End User" "Business Owner"
participant clientFe "Client Frontend" "Your App UI"
participant clientBe "Client Backend" "Your Server"
participant frankieOne "FrankieOne" "API / Platform"
participant dataSources "Data Sources" "ASIC, ABR..."

endUser->clientFe: Enters platform, fills in business details (ABN)

clientFe->clientBe: Submit KYB data (ABN, company name, director details)

clientBe->frankieOne: Look up business by ABN
  POST https://api.uat.frankie.one/v2/organizations/lookup
  >{"organizationNumber":{"registrationNumber":"51824753556","registryCode":"ABN"},"region":{"country":"AUS"}}
  <{"matchedOrganizations":[{"organizationToken":"org-tok-xxx","name":"Acme Pty Ltd"}]}

frankieOne-->clientBe: Return organization details

clientBe->frankieOne: Create organisation entity
  POST https://api.uat.frankie.one/v2/organizations
  >{"organizationToken":"org-tok-xxx"}
  <{"organization":{"entityId":"BF-org-1234..."}}

frankieOne-->clientBe: Return entityId

self clientBe: Store entityId → businessId mapping

clientBe->frankieOne: Execute KYB workflow
  POST https://api.uat.frankie.one/v2/organizations/{entityId}/serviceprofiles/{serviceName}/workflows/{workflowName}/execute

frankieOne-->clientBe: 202 Accepted — processing asynchronously

self frankieOne: Execute configured KYB workflow

group KYB workflow checks
  frankieOne->dataSources: Business entity verification — ASIC, ABR, state registries
  dataSources-->frankieOne: Return business data
  frankieOne->dataSources: AML check on business + UBOs
  dataSources-->frankieOne: Return AML results
end

frankieOne->>clientBe: Webhook — KYB workflow execution complete
  POST https://your-server.com/webhook/frankieone

clientBe->frankieOne: Fetch full KYB results for entityId
  GET https://api.uat.frankie.one/v2/organizations/{entityId}

frankieOne-->clientBe: Return KYB workflow execution results

self clientBe: Store KYB result in DB`,
  },
  {
    id: 'embedded-onesdk',
    label: 'Embedded OneSDK',
    description: 'FrankieOne SDK embedded directly in your web app UI',
    dsl: `title Embedded OneSDK — KYC Flow

participant endUser "End User" "Customer"
participant clientFe "Client Frontend" "Your App UI"
participant clientBe "Client Backend" "Your Server"
participant frankieOne "FrankieOne" "API / Platform"
participant dataSources "Data Sources" "DVS, Experian..."

endUser->clientFe: Enters platform and starts onboarding flow

clientFe->clientBe: Request session token to boot SDK

clientBe->frankieOne: Generate SDK session token
  POST https://api.uat.frankie.one/v2/auth/session
  >{"appRef":"your-internal-user-id"}
  <{"sessionKey":"eyJhbGci...","entityId":"BF-abc123...","expiresAt":"2024-01-15T11:30:00Z"}

frankieOne-->clientBe: Return session token + entityId

self clientBe: Store entityId → userId mapping

clientBe-->clientFe: Return session token + entityId

self clientFe: Initialise OneSDK — await before mounting

---

self clientFe: Mount WELCOME component → show welcome screen to user
clientFe->endUser: Welcome screen rendered

endUser->clientFe: User clicks Start
note clientFe: Event: form:welcome:ready | unmount WELCOME → mount CONSENT

self clientFe: Mount CONSENT component → show privacy consent
clientFe->endUser: Consent screen rendered

endUser->clientFe: User agrees to consent
note clientFe: Event: form:consent:ready | unmount CONSENT → mount IDV flow

self clientFe: instance.flow('idv').mount() → camera / document capture begins
clientFe->endUser: Document capture + liveness screen rendered

endUser->clientFe: Completes document scan and liveness check
note clientFe: Event: detection_complete | call instance.individual().submit({ verify: true })

---

clientFe->frankieOne: Submit biometric + document data via SDK

self frankieOne: Execute configured workflow on submitted data

group Workflow checks
  frankieOne->dataSources: KYC — DVS identity check
  dataSources-->frankieOne: DVS result
  frankieOne->dataSources: KYC — Experian credit bureau (if DVS insufficient)
  dataSources-->frankieOne: Experian result
  frankieOne->dataSources: KYC — Equifax credit bureau (if still insufficient)
  dataSources-->frankieOne: Equifax result
  frankieOne->dataSources: KYC — Superchoice superannuation (final)
  dataSources-->frankieOne: Superchoice result
  frankieOne->dataSources: AML — PEPs check
  dataSources-->frankieOne: PEPs result
  frankieOne->dataSources: AML — Sanctions check
  dataSources-->frankieOne: Sanctions result
end

frankieOne->clientFe: SDK emits results event (SUCCESS / FAIL / PENDING)
note clientFe: Event: results | navigate to outcome screen

frankieOne->>clientBe: Webhook — workflow execution complete
  POST https://your-server.com/webhook/frankieone

clientBe->frankieOne: Fetch full results for entityId
  GET https://api.uat.frankie.one/v2/individuals/{entityId}

frankieOne-->clientBe: Return workflow execution results

self clientBe: Store result in DB

clientFe->endUser: Show verification outcome (pass / fail / refer)`,
  },
]

// Keep the first template as the named export for backward compat
export const DSL_TEMPLATE = DIAGRAM_TEMPLATES[0].dsl
